import React from 'react';
import { Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { WebStep } from '@upstart/harmony/lib/legos/constants';
import { HookFormTextField } from '@upstart/commander-components';
import styles from './Content.module.scss';
import {
  CheckboxSrcType,
  ShareDealDialogDealConfig,
  ShareDealDialogSendConfig,
  ShareDealDialogStickerConfig,
} from './types';
import SendSection from './SendSection';
import DealConfigSection from './DealConfigSection';
import { FormSchema } from '../../schema';
import StickerConfigSection from './StickerConfigSection';
import messages from '../../messages.json';

export interface ShareDealDialogFormProps<
  StepVal extends CheckboxSrcType = WebStep,
  OfferVal extends CheckboxSrcType = string,
  D = Date,
> {
  customerMessage: string;
  onCustomerMessageChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;

  sendConfig: ShareDealDialogSendConfig;
  onSendConfigChange: (newSendConfig: ShareDealDialogSendConfig) => void;

  dealConfig: ShareDealDialogDealConfig<StepVal>;
  onDealConfigChange: (
    newDealConfig: ShareDealDialogDealConfig<StepVal>,
  ) => void;

  stickerConfig: ShareDealDialogStickerConfig<OfferVal, D>;
  onStickerConfigChange: (
    newStickerConfig: ShareDealDialogStickerConfig<OfferVal, D>,
  ) => void;
}

interface ComponentProps<
  StepVal extends CheckboxSrcType = WebStep,
  OfferVal extends CheckboxSrcType = string,
  D = Date,
> extends ShareDealDialogFormProps<OfferVal, StepVal, D> {
  onPreviewStickerClick: () => void;
}

const FormStep = <
  StepVal extends CheckboxSrcType = WebStep,
  OfferVal extends CheckboxSrcType = string,
  D = Date,
>({
  onPreviewStickerClick,
  customerMessage,
  onCustomerMessageChange,
  sendConfig,
  onSendConfigChange,
  dealConfig,
  onDealConfigChange,
  stickerConfig,
  onStickerConfigChange,
}: ComponentProps<StepVal, OfferVal, D>) => {
  const { control } = useFormContext<FormSchema>();

  return (
    <>
      <div className={styles.contentSection}>
        <Typography
          variant="h6"
          sx={{ mb: '8px' }}
          id="desking-share-deal-dialog-message-label"
        >
          {messages.formStep.messageTitle}
        </Typography>
        <HookFormTextField
          aria-labelledby="desking-share-deal-dialog-message-label"
          multiline
          variant="outlined"
          maxRows={10}
          value={customerMessage}
          onChange={onCustomerMessageChange}
          fullWidth
          showIconOnFormError={false}
          control={control}
          name="customerMessage"
        />
      </div>

      <SendSection config={sendConfig} onChange={onSendConfigChange} />

      <DealConfigSection config={dealConfig} onChange={onDealConfigChange} />

      <StickerConfigSection
        config={stickerConfig}
        onChange={onStickerConfigChange}
        onPreviewStickerClick={onPreviewStickerClick}
      />
    </>
  );
};

export default FormStep;
